// ~/components/HotelCategoryBadge.jsx  (icon-trigger version)
import React, { useState, useRef, useEffect } from "react";
import { InformationCircleIcon, XIcon } from "@heroicons/react/outline";

const COPY = {
	casual: {
		title: "Casual",
		desc:
			"True to their heritage and character, small luxury and boutique hotels that reflect local ethos are listed as Casual Inns. They hold within them, an unassuming charm and regional character that is an extension of the destination itself. Most of them fall under 3-star category with a possible mix of 4-star & homestay.",
	},
	casual_plus: {
		title: "Casual Plus",
		desc:
			"Casual+ accommodations include properties that stand out for their character, heritage, and exceptional service. These include heritage hotels such quintas, pousadas, and paradores, family-run properties, manor houses, winery hotels, and small luxury hotels that are the best in their class. They are usually 4-star hotels with the occasional 3-star property as well.",
	},
	premiere: {
		title: "Premiere",
		desc:
			"Premiere accommodations include some of the most prestigious hotels. Heritage properties, art décor contemporary places or chic boutique stays that are a notch above the rest are a part of this list. Here, you can expect spa experience, swimming pools and excellent service.",
	},
};

const normalizeKey = (v = "") =>
	v
		.toString()
		.trim()
		.toLowerCase()
		.replace(/\s*\+\s*/g, "_plus")
		.replace(/\s+/g, "_");

export default function HotelCategoryBadge({ category }) {
	const key = normalizeKey(category);
	const cfg = COPY[key];
	if (!cfg) return null;

	const [open, setOpen] = useState(false);
	const btnRef = useRef(null);
	const popRef = useRef(null);

	// Close on outside click / ESC
	useEffect(() => {
		function onDocClick(e) {
			if (!open) return;
			const inPanel = popRef.current?.contains(e.target);
			const inBtn = btnRef.current?.contains(e.target);
			if (!inPanel && !inBtn) setOpen(false);
		}
		function onEsc(e) {
			if (e.key === "Escape") setOpen(false);
		}
		document.addEventListener("mousedown", onDocClick);
		document.addEventListener("keydown", onEsc);
		return () => {
			document.removeEventListener("mousedown", onDocClick);
			document.removeEventListener("keydown", onEsc);
		};
	}, [open]);

	// Close when focus leaves both button and panel
	useEffect(() => {
		function onFocusIn(e) {
			if (!open) return;
			const t = e.target;
			const inBtn = btnRef.current?.contains(t);
			const inPop = popRef.current?.contains(t);
			if (!inBtn && !inPop) setOpen(false);
		}
		document.addEventListener("focusin", onFocusIn);
		return () => document.removeEventListener("focusin", onFocusIn);
	}, [open]);

	// Render "Casual+" nicely in the panel title
	const TitleLabel = () =>
		key === "casual_plus" ? (
			<span>
				Casual<span className="text-yellow-600 font-semibold">+</span>
			</span>
		) : (
			<span>{cfg.title}</span>
		);

	return (
		<span className="relative inline-flex">
			{/* Icon trigger */}
			<button
				ref={btnRef}
				type="button"
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-label={`${cfg.title} hotels info`}
				onClick={() => setOpen((v) => !v)}
				onKeyDown={(e) => {
					if (e.key === " " || e.key === "Enter") {
						e.preventDefault();
						setOpen((v) => !v);
					}
				}}
				className="-mt-0.5 inline-flex focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
			>
				<InformationCircleIcon className="h-4 w-4 text-primary" />
			</button>

			{open && (
				<div
					ref={popRef}
					role="dialog"
					aria-modal="false"
					tabIndex={-1}
					className="absolute z-50 w-72 max-w-[80vw] rounded-xl border border-gray-200 bg-white shadow-lg p-4 text-sm focus:outline-none 
               top-1/2 left-1/2 transform -translate-x-1/3 lg:-translate-x-1/2 -translate-y-1/2"
				>
					{/* Close (X) */}
					<button
						type="button"
						onClick={() => setOpen(false)}
						aria-label="Close"
						className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 focus:outline-none"
					>
						<XIcon className="h-4 w-4" />
					</button>

					<div className="font-semibold text-gray-900 mb-1">
						<TitleLabel /> Hotels
					</div>
					<p className="text-sm text-gray-500 lowercase">{cfg.desc}</p>
				</div>
			)}
		</span>
	);
}
